import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { num } from "@owners/format";
import { api } from "../api";

/**
 * Admin → Document import (V016): SharePoint / Teams folders read into the
 * Dataroom. Register a folder, scan it, correct the proposals (here or in the
 * exported sheet), approve, import. A nightly sync re-scans enabled sources.
 */
interface Source {
  id: string; label: string; siteUrl: string; folderPath: string; autoImport: boolean; enabled: boolean;
  lastScanAt: string | null; lastError: string | null; counts: Record<string, number>;
  scanning?: boolean; lastScanCounts?: { files: number; new: number; changed: number; removed: number } | null;
}
interface Item {
  id: string; path: string; fileName: string; size: number; hotelId: string | null; folder: string | null; docType: string | null;
  period: string | null; status: string; classifiedBy: string; note: string | null;
}
interface ItemsResp {
  items: Item[]; total: number; folders: string[];
  hotels: Array<{ id: string; code: string | null; name: string }>;
  types: Array<{ key: string; folder: string; label: string; cadence: string }>;
}
const FOLDER_LABEL: Record<string, string> = {
  financial_reports: "Financial Reports", reo_documents: "REO Documents", compliance: "Compliance", debt_compliance: "Debt Compliance", tax: "Tax",
};
const STATUS = ["proposed", "approved", "imported", "skipped", "error", "removed"] as const;

export function DocumentImport({ clientId }: { clientId: string }) {
  const qc = useQueryClient();
  const sources = useQuery({ queryKey: ["import-sources", clientId], queryFn: () => api<{ sources: Source[]; graphConfigured: boolean }>(`/api/admin/import/sources?clientId=${clientId}`),
    // A scan runs in the background (V017): poll until it finishes, then refresh the files.
    refetchInterval: (q) => (q.state.data?.sources.some((s) => s.scanning) ? 4000 : false) });
  const wasScanning = useRef(false);
  useEffect(() => {
    const now = !!sources.data?.sources.some((s) => s.scanning);
    if (wasScanning.current && !now) void qc.invalidateQueries({ queryKey: ["import-items"] });
    wasScanning.current = now;
  }, [sources.data, qc]);
  const [sourceId, setSourceId] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const current = sources.data?.sources.find((s) => s.id === sourceId) ?? sources.data?.sources[0] ?? null;
  const refresh = () => { void qc.invalidateQueries({ queryKey: ["import-sources", clientId] }); void qc.invalidateQueries({ queryKey: ["import-items"] }); };

  return (
    <div className="card">
      {sources.data && !sources.data.graphConfigured && (
        <p className="error">Microsoft Graph isn't set up yet — run scripts/graph-dataroom-setup.ps1 and secrets-setup.sh graph (docs/DATAROOM_IMPORT.md).</p>
      )}
      <div className="row" style={{ justifyContent: "space-between", marginBottom: 10 }}>
        <div className="row">
          {sources.data?.sources.map((s) => (
            <button key={s.id} className={current?.id === s.id ? "btn" : "btn secondary"} onClick={() => setSourceId(s.id)}>{s.label}</button>
          ))}
          {!sources.data?.sources.length && <span className="muted">No folders registered yet.</span>}
        </div>
        <span className="row">
          {(sources.data?.sources.filter((s) => s.enabled).length ?? 0) > 1 && (
            <button className="btn secondary" title="Scan every source that's on, one after another, in the background"
              onClick={async () => { await api(`/api/admin/import/scan-all?clientId=${clientId}`, { method: "POST" }); refresh(); }}>Scan all</button>
          )}
          <button className="btn" onClick={() => setAdding(true)}>+ Add folder</button>
        </span>
      </div>
      {adding && <AddSource clientId={clientId} onClose={(id) => { setAdding(false); if (id) { setSourceId(id); refresh(); } }} />}
      {current && <SourcePanel source={current} onChange={refresh} />}
      <p className="muted" style={{ fontSize: 12 }}>
        Each file gets a proposed hotel, folder, document type and period from its folder path and name. Check them, approve, then import.
        Typed files fill the hotel's Dataroom items (a newer file replaces the current version; an older one is filed under Imported/Earlier versions).
        Files without a type go to the folder's Imported subfolder. Nothing is deleted in Teams.
      </p>
    </div>
  );
}

function AddSource({ clientId, onClose }: { clientId: string; onClose: (id?: string) => void }) {
  const [label, setLabel] = useState(""), [siteUrl, setSiteUrl] = useState(""), [folderPath, setFolderPath] = useState("");
  const add = useMutation({
    mutationFn: () => api<{ source: { id: string } }>("/api/admin/import/sources", { method: "POST", body: { clientId, label, siteUrl, folderPath } }),
    onSuccess: (r) => onClose(r.source.id),
  });
  return (
    <div className="card" style={{ marginBottom: 12 }}>
      <div className="field"><label>Name</label><input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Asset Management team files" /></div>
      <div className="field"><label>SharePoint site address</label><input value={siteUrl} onChange={(e) => setSiteUrl(e.target.value)} placeholder="https://olivercompanies.sharepoint.com/sites/AssetManagement" /></div>
      <div className="field"><label>Folder in the site's Documents library (blank = all of it)</label><input value={folderPath} onChange={(e) => setFolderPath(e.target.value)} placeholder="General/Hotels" /></div>
      {add.error && <p className="error">{(add.error as Error).message}</p>}
      <div className="row"><button className="btn" disabled={add.isPending} onClick={() => add.mutate()}>Add</button><button className="btn secondary" onClick={() => onClose()}>Cancel</button></div>
    </div>
  );
}

function SourcePanel({ source, onChange }: { source: Source; onChange: () => void }) {
  const [status, setStatus] = useState<string>("proposed");
  const [q, setQ] = useState("");
  const [offset, setOffset] = useState(0);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [msg, setMsg] = useState<string | null>(null);
  const items = useQuery({
    queryKey: ["import-items", source.id, status, q, offset],
    queryFn: () => api<ItemsResp>(`/api/admin/import/sources/${source.id}/items?status=${status}&q=${encodeURIComponent(q)}&offset=${offset}`),
  });
  const hotelName = useMemo(() => new Map(items.data?.hotels.map((h) => [h.id, h.code ? `${h.code} · ${h.name}` : h.name])), [items.data]);
  const done = (m: string) => { setMsg(m); setPicked(new Set()); onChange(); };
  const call = useMutation({ mutationFn: (f: () => Promise<string>) => f(), onSuccess: done, onError: (e) => setMsg((e as Error).message) });

  const scan = () => call.mutate(async () => {
    await api(`/api/admin/import/sources/${source.id}/scan`, { method: "POST" });
    return "Scanning in the background — this updates when it's done (a whole team library can take a few minutes).";
  });
  const c = source.lastScanCounts;
  const setItems = (set: Record<string, unknown>, ids = [...picked]) => call.mutate(async () => {
    if (!ids.length) return "Pick files first";
    const r = await api<{ updated: number }>("/api/admin/import/items", { method: "PATCH", body: { ids, set } });
    return `${num(r.updated)} updated`;
  });
  const approveReady = () => call.mutate(async () => `${num((await api<{ approved: number }>(`/api/admin/import/sources/${source.id}/approve-ready`, { method: "POST" })).approved)} approved`);
  const runImport = () => call.mutate(async () => {
    let total = { imported: 0, duplicates: 0, failed: 0 };
    for (let i = 0; i < 50; i++) {      // 100 a call; keep going while there's progress
      const r = await api<{ imported: number; duplicates: number; failed: number; remaining: number }>(`/api/admin/import/sources/${source.id}/import`, { method: "POST" });
      total = { imported: total.imported + r.imported, duplicates: total.duplicates + r.duplicates, failed: total.failed + r.failed };
      setMsg(`Importing… ${num(total.imported)} done, ${num(r.remaining)} to go`);
      if (!r.remaining || r.imported + r.duplicates === 0) break;
    }
    return `${num(total.imported)} imported · ${num(total.duplicates)} already there · ${num(total.failed)} failed`;
  });
  const uploadSheet = (file: File) => call.mutate(async () => {
    const rows = parseCsv(await file.text());
    const r = await api<{ updated: number; problems: string[] }>(`/api/admin/import/sources/${source.id}/mapping`, { method: "POST", body: { rows } });
    return `${num(r.updated)} rows applied${r.problems.length ? ` · ${r.problems.length} problems: ${r.problems.slice(0, 5).join("; ")}` : ""}`;
  });
  const toggleSource = (body: Record<string, unknown>) => call.mutate(async () => { await api(`/api/admin/import/sources/${source.id}`, { method: "PATCH", body }); return "Saved"; });

  const pageIds = items.data?.items.map((i) => i.id) ?? [];
  const typesFor = (folder: string | null) => items.data?.types.filter((t) => !folder || t.folder === folder) ?? [];

  return (
    <>
      <div className="row" style={{ marginBottom: 8, flexWrap: "wrap" }}>
        <span className="muted" style={{ fontSize: 12 }}>{source.siteUrl}{source.folderPath ? ` / ${source.folderPath}` : ""}</span>
        <span className="muted" style={{ fontSize: 12 }}>· {source.scanning ? "scanning…" : `last scan ${source.lastScanAt ? new Date(source.lastScanAt).toLocaleString("en-US") : "never"}`}
          {!source.scanning && c ? ` · ${num(c.files)} files, ${num(c.new)} new, ${num(c.changed)} changed, ${num(c.removed)} gone` : ""}</span>
        {source.lastError && <span className="error" style={{ fontSize: 12 }}>{source.lastError}</span>}
        {!source.enabled && <span className="muted" style={{ fontSize: 12 }}>· off (split into channels, or nightly sync off)</span>}
        {source.enabled && <button className="btn link" style={{ fontSize: 12 }} disabled={call.isPending}
          title="Add one source per channel (top-level folder) and switch this one off — for libraries too big to scan whole"
          onClick={() => window.confirm(`Add one source per channel under ${source.label} and switch this one off?`) && call.mutate(async () =>
            `${num((await api<{ created: number }>(`/api/admin/import/sources/${source.id}/split`, { method: "POST" })).created)} channel sources added — scan each one`)}>Split into channels</button>}
      </div>
      <div className="row" style={{ marginBottom: 10, flexWrap: "wrap" }}>
        <button className="btn" disabled={call.isPending || !!source.scanning} onClick={scan}>{source.scanning ? "Scanning…" : "Scan now"}</button>
        <button className="btn secondary" disabled={call.isPending} title="Complete proposals that fill a Dataroom item (have a type) — not the archive"
          onClick={() => call.mutate(async () => `${num((await api<{ approved: number }>(`/api/admin/import/sources/${source.id}/approve-ready?typed=1`, { method: "POST" })).approved)} typed files approved`)}>Approve complete · typed only</button>
        <button className="btn secondary" disabled={call.isPending} onClick={approveReady}>Approve all complete</button>
        <button className="btn secondary" disabled={call.isPending} title="Re-run the proposals for files nobody has checked (after the rules or hotels' legal entities change)"
          onClick={() => call.mutate(async () => `${num((await api<{ reproposed: number }>(`/api/admin/import/sources/${source.id}/repropose`, { method: "POST" })).reproposed)} files re-proposed`)}>Re-run proposals</button>
        <button className="btn" disabled={call.isPending || !source.counts.approved} onClick={runImport}>Import approved · {num(source.counts.approved ?? 0)}</button>
        <a className="btn secondary" href={`/api/admin/import/sources/${source.id}/export.csv`}>Download sheet</a>
        <label className="btn secondary" style={{ cursor: "pointer" }}>Upload corrected sheet
          <input type="file" accept=".csv" style={{ display: "none" }} onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadSheet(f); e.target.value = ""; }} />
        </label>
        <label className="row" style={{ fontSize: 12 }}><input type="checkbox" checked={source.autoImport} onChange={(e) => toggleSource({ autoImport: e.target.checked })} /> Nightly: import new files whose proposal is complete</label>
        <label className="row" style={{ fontSize: 12 }}><input type="checkbox" checked={source.enabled} onChange={(e) => toggleSource({ enabled: e.target.checked })} /> Nightly sync on</label>
      </div>
      {msg && <p className="muted" style={{ fontSize: 12 }}>{msg}</p>}
      <FolderMap sourceId={source.id} onChange={(m) => { setMsg(m); onChange(); }} />
      <div className="tabs">
        {STATUS.map((s) => <button key={s} className={status === s ? "on" : ""} onClick={() => { setStatus(s); setOffset(0); setPicked(new Set()); }}>{s[0]!.toUpperCase() + s.slice(1)} · {num(source.counts[s] ?? 0)}</button>)}
      </div>
      <div className="row" style={{ marginBottom: 8, flexWrap: "wrap" }}>
        <input placeholder="Filter by path" value={q} onChange={(e) => { setQ(e.target.value); setOffset(0); }} style={{ width: 220 }} />
        <span className="muted" style={{ fontSize: 12 }}>{num(picked.size)} picked</span>
        <select value="" onChange={(e) => e.target.value && setItems({ hotelId: e.target.value })}>
          <option value="">Set hotel…</option>
          {items.data?.hotels.map((h) => <option key={h.id} value={h.id}>{hotelName.get(h.id)}</option>)}
        </select>
        <select value="" onChange={(e) => e.target.value && setItems({ folder: e.target.value, docType: null, period: null })}>
          <option value="">Set folder (untyped)…</option>
          {items.data?.folders.map((f) => <option key={f} value={f}>{FOLDER_LABEL[f]}</option>)}
        </select>
        <select value="" onChange={(e) => e.target.value && setItems({ docType: e.target.value })}>
          <option value="">Set type…</option>
          {items.data?.types.map((t) => <option key={t.key} value={t.key}>{FOLDER_LABEL[t.folder]} · {t.label}</option>)}
        </select>
        <button className="btn secondary" onClick={() => setItems({ status: "approved" })}>Approve</button>
        <button className="btn secondary" onClick={() => setItems({ status: "skipped" })}>Skip</button>
        <button className="btn secondary" onClick={() => setItems({ status: "proposed" })}>Back to proposed</button>
      </div>
      <table>
        <thead><tr>
          <th><input type="checkbox" checked={pageIds.length > 0 && pageIds.every((id) => picked.has(id))}
            onChange={(e) => setPicked(e.target.checked ? new Set(pageIds) : new Set())} /></th>
          <th>File</th><th>Hotel</th><th>Folder</th><th>Type</th><th>Period</th><th>Note</th>
        </tr></thead>
        <tbody>
          {items.data?.items.map((it) => (
            <tr key={it.id}>
              <td><input type="checkbox" checked={picked.has(it.id)} onChange={(e) => { const n = new Set(picked); if (e.target.checked) n.add(it.id); else n.delete(it.id); setPicked(n); }} /></td>
              <td><strong>{it.fileName}</strong><div className="muted" style={{ fontSize: 11 }}>{it.path}</div></td>
              <td>{it.hotelId ? hotelName.get(it.hotelId) : <span className="error">—</span>}</td>
              <td>{it.folder ? FOLDER_LABEL[it.folder] : <span className="error">—</span>}</td>
              <td>
                {it.status === "imported" ? (items.data?.types.find((t) => t.key === it.docType)?.label ?? "Imported subfolder") : (
                  <select value={it.docType ?? ""} onChange={(e) => setItems({ docType: e.target.value || null }, [it.id])}>
                    <option value="">— none (Imported subfolder)</option>
                    {typesFor(it.folder).map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
                  </select>
                )}
              </td>
              <td>
                {it.docType && it.status !== "imported" ? (
                  <input defaultValue={it.period ?? ""} style={{ width: 90 }} placeholder={periodHint(items.data?.types.find((t) => t.key === it.docType)?.cadence)}
                    onBlur={(e) => e.target.value !== (it.period ?? "") && setItems({ period: e.target.value || null }, [it.id])} />
                ) : (it.period ?? "")}
              </td>
              <td className="muted" style={{ fontSize: 11 }}>{it.classifiedBy === "person" ? "checked · " : ""}{it.note ?? ""}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {items.data && items.data.total > 200 && (
        <div className="row" style={{ marginTop: 8 }}>
          <button className="btn secondary" disabled={offset === 0} onClick={() => setOffset(Math.max(offset - 200, 0))}>Previous</button>
          <span className="muted" style={{ fontSize: 12 }}>{num(offset + 1)}–{num(Math.min(offset + 200, items.data.total))} of {num(items.data.total)}</span>
          <button className="btn secondary" disabled={offset + 200 >= items.data.total} onClick={() => setOffset(offset + 200)}>Next</button>
        </div>
      )}
    </>
  );
}

function periodHint(cadence?: string) {
  return cadence === "monthly" ? "YYYY-MM" : cadence === "quarterly" ? "YYYY-Qn" : cadence === "annual" ? "YYYY" : "none";
}

/** Minimal CSV reader for the exported sheet (quoted cells, commas and newlines inside quotes). */
export function parseCsv(text: string): Array<Record<string, string>> {
  const rows: string[][] = [];
  let row: string[] = [], cell = "", inQ = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i]!;
    if (inQ) {
      if (c === '"' && text[i + 1] === '"') { cell += '"'; i++; }
      else if (c === '"') inQ = false;
      else cell += c;
    } else if (c === '"') inQ = true;
    else if (c === ",") { row.push(cell); cell = ""; }
    else if (c === "\n" || c === "\r") { if (c === "\r" && text[i + 1] === "\n") i++; row.push(cell); rows.push(row); row = []; cell = ""; }
    else cell += c;
  }
  if (cell || row.length) { row.push(cell); rows.push(row); }
  const [head, ...body] = rows.filter((r) => r.some((x) => x.trim()));
  if (!head) return [];
  const keys = head.map((h) => h.trim().toLowerCase());
  return body.map((r) => Object.fromEntries(keys.map((k, i) => [k, (r[i] ?? "").trim()])));
}

interface FolderRow { folder: string; files: number; imported: number; withHotel: number; action: "hotel" | "skip" | "mixed"; hotelId: string | null }

/**
 * Channels → hotels (V017). Owner Drive has a channel per ownership LLC, so the
 * folder names don't say which hotel. Map each once: a hotel, "not a hotel"
 * (skip its files), or "several hotels" (each file on its own).
 */
function FolderMap({ sourceId, onChange }: { sourceId: string; onChange: (msg: string) => void }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const q = useQuery({ queryKey: ["import-folders", sourceId], enabled: open,
    queryFn: () => api<{ folders: FolderRow[]; hotels: Array<{ id: string; code: string | null; name: string }> }>(`/api/admin/import/sources/${sourceId}/folders`) });
  const save = useMutation({
    mutationFn: () => api<{ reproposed: number }>(`/api/admin/import/sources/${sourceId}/folders`, { method: "PUT", body: {
      rules: Object.entries(draft).map(([folder, v]) => v === "skip" || v === "mixed" ? { folder, action: v } : { folder, action: "hotel", hotelId: v }),
    } }),
    onSuccess: (r) => { setDraft({}); void qc.invalidateQueries({ queryKey: ["import-folders", sourceId] }); onChange(`${num(r.reproposed)} files re-proposed`); },
    onError: (e) => onChange((e as Error).message),
  });
  const value = (f: FolderRow) => draft[f.folder] ?? (f.action === "hotel" ? f.hotelId! : f.action);
  const unmapped = q.data?.folders.filter((f) => f.action === "mixed").length ?? 0;
  return (
    <div style={{ margin: "6px 0 12px" }}>
      <button className="btn link" onClick={() => setOpen(!open)}>{open ? "▾" : "▸"} Channels → hotels{q.data ? ` · ${num(q.data.folders.length)} folders, ${num(unmapped)} not mapped` : ""}</button>
      {open && q.data && (
        <>
          <div className="row" style={{ margin: "6px 0", flexWrap: "wrap" }}>
            <button className="btn" disabled={!Object.keys(draft).length || save.isPending} onClick={() => save.mutate()}>Save mappings · {num(Object.keys(draft).length)}</button>
            <button className="btn secondary" onClick={() => downloadMap(q.data!)}>Download channel map</button>
            <label className="btn secondary" style={{ cursor: "pointer" }}>Upload channel map
              <input type="file" accept=".csv" style={{ display: "none" }} onChange={async (e) => {
                const f = e.target.files?.[0]; e.target.value = ""; if (!f) return;
                const { draft: d, problems } = readMap(await f.text(), q.data!);
                setDraft({ ...draft, ...d });
                onChange(`${num(Object.keys(d).length)} channel mappings loaded — check them, then Save mappings${problems.length ? ` · ${problems.slice(0, 5).join("; ")}` : ""}`);
              }} />
            </label>
            <span className="muted" style={{ fontSize: 12 }}>Applies to files not yet approved; a hotel you set on a file yourself is kept.</span>
          </div>
          <table style={{ marginTop: 6 }}>
            <thead><tr><th>Channel / top folder</th><th>Files</th><th>With a hotel</th><th>Hotel</th></tr></thead>
            <tbody>
              {q.data.folders.map((f) => (
                <tr key={f.folder}>
                  <td><strong>{f.folder}</strong></td>
                  <td>{num(f.files)}{f.imported ? ` · ${num(f.imported)} imported` : ""}</td>
                  <td>{num(f.withHotel)}</td>
                  <td>
                    <select value={value(f)} onChange={(e) => setDraft({ ...draft, [f.folder]: e.target.value })}>
                      <option value="mixed">Several hotels / decide per file</option>
                      <option value="skip">Not a hotel — skip its files</option>
                      {q.data!.hotels.map((h) => <option key={h.id} value={h.id}>{h.code ? `${h.code} · ${h.name}` : h.name}</option>)}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

        </>
      )}
    </div>
  );
}

/** Channel map sheet: folder, files, hotel — hotel is a hotel code, "skip" or "mixed". */
function downloadMap(d: { folders: FolderRow[]; hotels: Array<{ id: string; code: string | null; name: string }> }) {
  const code = new Map(d.hotels.map((h) => [h.id, h.code ?? h.name]));
  const cell = (v: unknown) => { const s = String(v ?? ""); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
  const lines = ["folder,files,hotel", ...d.folders.map((f) => [f.folder, f.files, f.action === "hotel" ? code.get(f.hotelId!) : f.action].map(cell).join(","))];
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([lines.join("\n")], { type: "text/csv" }));
  a.download = "channel-map.csv"; a.click(); URL.revokeObjectURL(a.href);
}
function readMap(text: string, d: { folders: FolderRow[]; hotels: Array<{ id: string; code: string | null; name: string }> }) {
  const byCode = new Map(d.hotels.flatMap((h) => [[(h.code ?? "").toUpperCase(), h.id], [h.name.toUpperCase(), h.id]] as Array<[string, string]>).filter(([k]) => k));
  const known = new Set(d.folders.map((f) => f.folder));
  const draft: Record<string, string> = {}; const problems: string[] = [];
  for (const r of parseCsv(text)) {
    const folder = r.folder ?? ""; const v = (r.hotel ?? "").trim();
    if (!folder || !known.has(folder)) { if (folder) problems.push(`unknown folder ${folder}`); continue; }
    const low = v.toLowerCase();
    if (low === "skip" || low === "not a hotel") draft[folder] = "skip";
    else if (!v || low === "mixed" || low === "several") draft[folder] = "mixed";
    else if (byCode.has(v.toUpperCase())) draft[folder] = byCode.get(v.toUpperCase())!;
    else problems.push(`${folder}: unknown hotel ${v}`);
  }
  return { draft, problems };
}
